// Zip-level .xlsx cell patcher.
//
// Re-serializing a workbook through xlsx-js-style drops most template
// formatting (merged cells, column widths, conditional formatting, themes…).
// An .xlsx is a zip of XML parts, so instead we patch only the mapped cells
// inside the worksheet XML and leave every other byte of the template
// untouched — formatting is preserved by construction.
//
// Scope: works on standard Excel-produced files (rows/cells carry explicit
// `r` address attributes, no namespace prefixes on worksheet elements).

import JSZip from "jszip";

export interface CellWrite {
  /** Worksheet name; null → the workbook's first sheet. */
  sheet: string | null;
  /** A1-style cell address, e.g. "D20". */
  cell: string;
  /** null clears the cell's content (style kept); formula cells are never cleared. */
  value: string | number | null;
  /** With value null: clear even a formula cell (caller has determined it's inert). */
  force?: boolean;
}

function escapeXml(text: string): string {
  return text.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

function unescapeXml(text: string): string {
  return text
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&apos;/g, "'")
    .replace(/&amp;/g, "&");
}

function colToIndex(col: string): number {
  let n = 0;
  for (const ch of col) n = n * 26 + (ch.charCodeAt(0) - 64);
  return n;
}

function splitAddr(addr: string): { col: string; row: number } {
  const m = addr.match(/^([A-Z]+)(\d+)$/);
  if (!m) throw new Error(`Invalid cell address "${addr}"`);
  return { col: m[1], row: Number(m[2]) };
}

// ---------------------------------------------------------------------------
// Read-path memoisation (ENG-2008)
//
// Every reader below used to call JSZip.loadAsync(buf) itself, so exporting one
// workbook inflated the same template ~18 times (readSheetCells alone: 10) and
// re-parsed its sheet XML with xmldom just as often — measured at 8.2 MB of
// redundant inflate per export. Supabase edge functions get a hard ~2s CPU
// budget per request, and on heavier properties that overhead was enough to get
// the worker killed outright: HTTP 546, "CPU Time exceeded", mid-generation.
//
// Everything here is keyed on the ArrayBuffer's identity. Each stage that
// rewrites the workbook returns NEW bytes, so a patched workbook can never read
// back a stale entry, and WeakMaps let the old bytes go as soon as the caller
// drops them.
//
// Readers only. insertSheetImages/stampXlsxTemplate mutate their zip before
// serialising it, so they must keep loading an instance of their own.
// ---------------------------------------------------------------------------

const zipByBuffer = new WeakMap<ArrayBuffer, Promise<JSZip>>();

/** JSZip.loadAsync, once per distinct workbook buffer. */
function loadZipForRead(buf: ArrayBuffer): Promise<JSZip> {
  const cached = zipByBuffer.get(buf);
  if (cached) return cached;
  // Cache the promise, not the result: concurrent readers of the same buffer
  // (the Summary fills all start from the same bytes) then share one inflate
  // instead of racing to start their own.
  const loading = JSZip.loadAsync(buf);
  zipByBuffer.set(buf, loading);
  return loading;
}

const sheetMapByZip = new WeakMap<JSZip, Promise<{ names: string[]; pathByName: Map<string, string> }>>();

/** readSheetMap, once per loaded zip — every reader needs it to resolve a name. */
function readSheetMapCached(zip: JSZip) {
  const cached = sheetMapByZip.get(zip);
  if (cached) return cached;
  const reading = readSheetMap(zip);
  sheetMapByZip.set(zip, reading);
  return reading;
}

const sheetCellsByBuffer = new WeakMap<ArrayBuffer, Map<string, Promise<Map<string, string>>>>();

const sharedStringsByBuffer = new WeakMap<ArrayBuffer, Promise<string[]>>();

/** Ordered sheet names + name → zip path of each worksheet part. */
async function readSheetMap(zip: JSZip): Promise<{ names: string[]; pathByName: Map<string, string> }> {
  const wbFile = zip.file("xl/workbook.xml");
  const relsFile = zip.file("xl/_rels/workbook.xml.rels");
  if (!wbFile || !relsFile) throw new Error("Not a valid .xlsx file (missing workbook parts).");
  const wbXml = await wbFile.async("string");
  const relsXml = await relsFile.async("string");

  const targetByRelId = new Map<string, string>();
  for (const rel of relsXml.matchAll(/<Relationship\b[^>]*>/g)) {
    const id = rel[0].match(/\bId="([^"]+)"/)?.[1];
    const target = rel[0].match(/\bTarget="([^"]+)"/)?.[1];
    if (!id || !target) continue;
    // Targets are workbook-relative ("worksheets/sheet1.xml") or package-absolute.
    targetByRelId.set(id, target.startsWith("/") ? target.slice(1) : `xl/${target}`);
  }

  const names: string[] = [];
  const pathByName = new Map<string, string>();
  for (const sheet of wbXml.matchAll(/<sheet\b[^>]*>/g)) {
    const rawName = sheet[0].match(/\bname="([^"]*)"/)?.[1];
    const relId = sheet[0].match(/\br:id="([^"]+)"/)?.[1];
    if (rawName === undefined || !relId) continue;
    const name = unescapeXml(rawName);
    const path = targetByRelId.get(relId);
    names.push(name);
    if (path) pathByName.set(name, path);
  }
  if (names.length === 0) throw new Error("No worksheets found in the Excel file.");
  return { names, pathByName };
}

/** Every dash variant → "-", collapse whitespace, lowercase. */
function normSheetName(name: string): string {
  return name.replace(/[-‐-―−]/g, "-").replace(/\s+/g, " ").trim().toLowerCase();
}

/**
 * Zip path of a sheet by name — exact first, then dash/whitespace/case-
 * insensitive. Excel (and clients editing the template) freely swap a hyphen
 * for an em dash in a tab name like "DG Leasing — Summary", which an exact
 * match would miss, silently dropping every write/read for that sheet.
 */
function resolveSheetPath(
  pathByName: Map<string, string>,
  sheetName: string
): string | undefined {
  const exact = pathByName.get(sheetName);
  if (exact) return exact;
  const target = normSheetName(sheetName);
  for (const [name, path] of pathByName) {
    if (normSheetName(name) === target) return path;
  }
  return undefined;
}

/** Sheet names of an .xlsx, in workbook order. */
export async function listXlsxSheetNames(buf: ArrayBuffer): Promise<string[]> {
  const zip = await loadZipForRead(buf);
  return (await readSheetMapCached(zip)).names;
}

/**
 * First sheet name (workbook order) matching `matcher`, or null. Used to locate
 * a sheet by a stable anchor instead of its exact title — the DG template's
 * summary tab has shipped as "DG Leasing - Summary", "… — Summary", and
 * "DG Leasing ONLY - Summary", but always ends in "Summary".
 */
export async function findSheetName(
  buf: ArrayBuffer,
  matcher: RegExp
): Promise<string | null> {
  const zip = await loadZipForRead(buf);
  const { names } = await readSheetMapCached(zip);
  return names.find((n) => matcher.test(n)) ?? null;
}

/**
 * Text of every cell of a sheet, keyed by A1 ref. Used to locate rule rows
 * and placeholder tokens instead of hardcoding addresses. Reading uses
 * DOMParser (writing must not — see patchSheetXml). Returns an empty map when
 * the sheet doesn't exist.
 */
export async function readSheetCells(
  buf: ArrayBuffer,
  sheetName: string
): Promise<Map<string, string>> {
  let bySheet = sheetCellsByBuffer.get(buf);
  if (!bySheet) {
    bySheet = new Map();
    sheetCellsByBuffer.set(buf, bySheet);
  }
  let reading = bySheet.get(sheetName);
  if (!reading) {
    reading = readSheetCellsUncached(buf, sheetName);
    bySheet.set(sheetName, reading);
  }
  // Copy: the cached map is handed to every caller, and one of them mutating
  // it would corrupt the others' reads. Trivial next to the parse it replaces.
  return new Map(await reading);
}

/**
 * The workbook's shared string table. Split out and memoised because it is
 * workbook-wide, not per-sheet: every readSheetCells call used to re-parse it,
 * and it is usually the largest XML part in the file.
 */
async function readSharedStrings(buf: ArrayBuffer, zip: JSZip): Promise<string[]> {
  const cached = sharedStringsByBuffer.get(buf);
  if (cached) return cached;
  const reading = (async () => {
    const sstFile = zip.file("xl/sharedStrings.xml");
    const shared: string[] = [];
    if (!sstFile) return shared;
    const sstDoc = new DOMParser().parseFromString(await sstFile.async("string"), "application/xml");
    // childNodes + element filter, not .children — the edge function's xmldom
    // DOMParser implements Node but not the Element.children collection.
    for (const si of Array.from(sstDoc.documentElement.childNodes)) {
      if (si.nodeType !== 1) continue;
      shared.push(
        Array.from((si as Element).getElementsByTagName("t"))
          .map((t) => t.textContent ?? "")
          .join("")
      );
    }
    return shared;
  })();
  sharedStringsByBuffer.set(buf, reading);
  return reading;
}

async function readSheetCellsUncached(
  buf: ArrayBuffer,
  sheetName: string
): Promise<Map<string, string>> {
  const zip = await loadZipForRead(buf);
  const { pathByName } = await readSheetMapCached(zip);
  const path = resolveSheetPath(pathByName, sheetName);
  const out = new Map<string, string>();
  if (!path || !zip.file(path)) return out;

  const parse = (xml: string) => new DOMParser().parseFromString(xml, "application/xml");
  const shared = await readSharedStrings(buf, zip);

  const doc = parse(await zip.file(path)!.async("string"));
  for (const c of Array.from(doc.getElementsByTagName("c"))) {
    const ref = c.getAttribute("r");
    if (!ref) continue;
    const t = c.getAttribute("t");
    let text = "";
    if (t === "s") {
      const idx = Number(c.getElementsByTagName("v")[0]?.textContent ?? -1);
      text = shared[idx] ?? "";
    } else if (t === "inlineStr") {
      text = Array.from(c.getElementsByTagName("t"))
        .map((el) => el.textContent ?? "")
        .join("");
    } else {
      text = c.getElementsByTagName("v")[0]?.textContent ?? "";
    }
    if (text.trim()) out.set(ref, text);
  }
  return out;
}

/** Column view over readSheetCells: row number → text for one column. */
export async function readSheetColumnText(
  buf: ArrayBuffer,
  sheetName: string,
  column: string
): Promise<Map<number, string>> {
  const cells = await readSheetCells(buf, sheetName);
  const out = new Map<number, string>();
  const cellRe = new RegExp(`^${column}(\\d+)$`);
  for (const [ref, text] of cells) {
    const m = ref.match(cellRe);
    if (m) out.set(Number(m[1]), text);
  }
  return out;
}

/**
 * Approximate pixel geometry of a sheet (column widths / row heights), for
 * computing the aspect ratio of cell-anchored boxes. Uses Excel's usual
 * width-chars ≈ ×7px and height-points ≈ ×4/3px conversions — close enough
 * for aspect math.
 */
export async function readSheetGeometry(
  buf: ArrayBuffer,
  sheetName: string
): Promise<{ colPx: (zeroBasedCol: number) => number; rowPx: (oneBasedRow: number) => number } | null> {
  const zip = await loadZipForRead(buf);
  const { pathByName } = await readSheetMapCached(zip);
  const path = resolveSheetPath(pathByName, sheetName);
  if (!path || !zip.file(path)) return null;
  const doc = new DOMParser().parseFromString(await zip.file(path)!.async("string"), "application/xml");

  // Missing attributes read as null in browsers but "" under xmldom (the edge
  // DOMParser) — and Number("") is 0, which would register phantom zero
  // heights/widths. numAttr treats both absences the same.
  const numAttr = (el: { getAttribute(name: string): string | null }, name: string): number => {
    const raw = el.getAttribute(name);
    return raw ? Number(raw) : NaN;
  };

  const fmt = doc.getElementsByTagName("sheetFormatPr")[0];
  const fmtColWidth = fmt ? numAttr(fmt, "defaultColWidth") : NaN;
  const fmtRowHeight = fmt ? numAttr(fmt, "defaultRowHeight") : NaN;
  const defaultColWidth = Number.isFinite(fmtColWidth) ? fmtColWidth : 8.43;
  const defaultRowHeight = Number.isFinite(fmtRowHeight) ? fmtRowHeight : 15;

  const colWidths = new Map<number, number>();
  for (const col of Array.from(doc.getElementsByTagName("col"))) {
    const min = numAttr(col, "min");
    const max = numAttr(col, "max");
    const width = numAttr(col, "width");
    if (!Number.isFinite(min) || !Number.isFinite(max) || !Number.isFinite(width)) continue;
    for (let i = min; i <= Math.min(max, min + 500); i++) colWidths.set(i, width);
  }
  const rowHeights = new Map<number, number>();
  const hiddenRows = new Set<number>();
  for (const row of Array.from(doc.getElementsByTagName("row"))) {
    const r = numAttr(row, "r");
    if (!Number.isFinite(r)) continue;
    const ht = numAttr(row, "ht");
    if (Number.isFinite(ht)) rowHeights.set(r, ht);
    // A hidden row (the DG template's macro-driven show/hide sections) keeps
    // its `ht` in the XML but renders at 0px in Excel — summing its stored
    // height into a multi-row box (photo slots) would inflate the box past
    // what the sheet actually shows.
    if (row.getAttribute("hidden") === "1") hiddenRows.add(r);
  }

  return {
    colPx: (c) => Math.round((colWidths.get(c + 1) ?? defaultColWidth) * 7),
    rowPx: (r) => (hiddenRows.has(r) ? 0 : Math.round(((rowHeights.get(r) ?? defaultRowHeight) * 4) / 3)),
  };
}

/** Formula text of every formula cell in one column of a sheet, keyed by row. */
export async function readSheetColumnFormulas(
  buf: ArrayBuffer,
  sheetName: string,
  column: string
): Promise<Map<number, string>> {
  const zip = await loadZipForRead(buf);
  const { pathByName } = await readSheetMapCached(zip);
  const path = resolveSheetPath(pathByName, sheetName);
  const out = new Map<number, string>();
  if (!path || !zip.file(path)) return out;

  const doc = new DOMParser().parseFromString(await zip.file(path)!.async("string"), "application/xml");
  const cellRe = new RegExp(`^${column}(\\d+)$`);
  for (const c of Array.from(doc.getElementsByTagName("c"))) {
    const m = c.getAttribute("r")?.match(cellRe);
    if (!m) continue;
    const f = c.getElementsByTagName("f")[0]?.textContent;
    if (f?.trim()) out.set(Number(m[1]), f);
  }
  return out;
}

function buildCellXml(ref: string, value: string | number, styleAttr: string): string {
  return typeof value === "number"
    ? `<c r="${ref}"${styleAttr}><v>${value}</v></c>`
    : `<c r="${ref}"${styleAttr} t="inlineStr"><is><t xml:space="preserve">${escapeXml(value)}</t></is></c>`;
}

/**
 * Locates the full `<row>`/`<c>` element with the given `r` address. Matching
 * the opening tag first and only then scanning for the close tag is what makes
 * self-closing elements safe: a single regex alternation like
 * `(?:\/>|>[\s\S]*?<\/c>)` backtracks past a self-closing cell's `/` and
 * swallows every sibling up to the row's first real `</c>`.
 */
function matchElement(
  xml: string,
  tag: "row" | "c",
  ref: string | number
): { start: number; text: string; openTag: string } | null {
  const open = xml.match(new RegExp(`<${tag}\\b[^>]*\\br="${ref}"[^>]*>`));
  if (open?.index === undefined) return null;
  const openTag = open[0];
  if (openTag.endsWith("/>")) return { start: open.index, text: openTag, openTag };
  const closeTag = `</${tag}>`;
  const close = xml.indexOf(closeTag, open.index + openTag.length);
  if (close < 0) return null;
  return { start: open.index, text: xml.slice(open.index, close + closeTag.length), openTag };
}

function spliceAt(xml: string, start: number, oldLength: number, replacement: string): string {
  return xml.slice(0, start) + replacement + xml.slice(start + oldLength);
}

function patchSheetXml(
  xml: string,
  patches: Array<{ cell: string; value: string | number | null; force?: boolean }>
): { xml: string; formulaTouched: boolean } {
  let formulaTouched = false;

  for (const patch of patches) {
    const { col, row } = splitAddr(patch.cell);
    const rowEl = matchElement(xml, "row", row);

    // A clear only empties an existing non-formula cell — an absent cell is
    // already blank, and template formulas survive unless the caller forces
    // the clear after determining the formula is inert.
    if (patch.value === null) {
      if (!rowEl) continue;
      const cellEl = matchElement(rowEl.text, "c", patch.cell);
      if (!cellEl) continue;
      if (cellEl.text.includes("<f")) {
        if (!patch.force) continue;
        formulaTouched = true;
      }
      const style = cellEl.openTag.match(/\bs="([^"]*)"/);
      const emptyCell = `<c r="${patch.cell}"${style ? ` s="${style[1]}"` : ""}/>`;
      const newRowXml = spliceAt(rowEl.text, cellEl.start, cellEl.text.length, emptyCell);
      xml = spliceAt(xml, rowEl.start, rowEl.text.length, newRowXml);
      continue;
    }

    if (!rowEl) {
      // Row absent: insert a new one, keeping rows ordered by index.
      const newRow = `<row r="${row}">${buildCellXml(patch.cell, patch.value, "")}</row>`;
      let insertAt = -1;
      for (const m of xml.matchAll(/<row\b[^>]*\br="(\d+)"/g)) {
        if (Number(m[1]) > row) {
          insertAt = m.index;
          break;
        }
      }
      if (insertAt >= 0) {
        xml = xml.slice(0, insertAt) + newRow + xml.slice(insertAt);
      } else if (xml.includes("</sheetData>")) {
        xml = xml.replace("</sheetData>", `${newRow}</sheetData>`);
      } else {
        xml = xml.replace(/<sheetData\s*\/>/, `<sheetData>${newRow}</sheetData>`);
      }
      continue;
    }

    const rowXml = rowEl.text;
    let newRowXml: string;
    const cellEl = matchElement(rowXml, "c", patch.cell);

    if (cellEl) {
      // Replace the cell's content but keep its style index (s attribute) so
      // the template's formatting for that cell survives.
      if (cellEl.text.includes("<f")) formulaTouched = true;
      const style = cellEl.openTag.match(/\bs="([^"]*)"/);
      newRowXml = spliceAt(
        rowXml,
        cellEl.start,
        cellEl.text.length,
        buildCellXml(patch.cell, patch.value, style ? ` s="${style[1]}"` : "")
      );
    } else {
      // Cell absent in an existing row: insert it, keeping cells column-ordered.
      const newCell = buildCellXml(patch.cell, patch.value, "");
      if (rowXml.endsWith("/>")) {
        // Self-closing (empty) row: expand it.
        newRowXml = `${rowXml.slice(0, -2)}>${newCell}</row>`;
      } else {
        let insertAt = -1;
        for (const m of rowXml.matchAll(/<c\b[^>]*\br="([A-Z]+)\d+"/g)) {
          if (colToIndex(m[1]) > colToIndex(col)) {
            insertAt = m.index;
            break;
          }
        }
        newRowXml =
          insertAt >= 0
            ? rowXml.slice(0, insertAt) + newCell + rowXml.slice(insertAt)
            : rowXml.replace(/<\/row>$/, () => `${newCell}</row>`);
      }
    }
    xml = spliceAt(xml, rowEl.start, rowXml.length, newRowXml);
  }

  return { xml, formulaTouched };
}

/**
 * A stamped-over formula leaves the workbook's calc chain pointing at a cell
 * that no longer has a formula, which makes Excel warn about unreadable
 * content. Dropping the part (and its references) is safe — Excel rebuilds it.
 */
/**
 * Excel's legacy worksheet-protection password hash (16-bit). Weak by design —
 * sheet protection is a tamper deterrent, not encryption — but it makes Excel
 * prompt for the password before Unprotect Sheet, which is what "lock so no one
 * edits it" asks for. Algorithm per MS-OFFCRYPTO / the classic VBA hash.
 */
function legacyPasswordHash(password: string): string {
  let hash = 0;
  for (let i = password.length - 1; i >= 0; i--) {
    hash = ((hash >> 14) & 0x01) | ((hash << 1) & 0x7fff);
    hash ^= password.charCodeAt(i);
  }
  hash = ((hash >> 14) & 0x01) | ((hash << 1) & 0x7fff);
  hash ^= 0xce4b; // 0x8000 | ('N'<<8) | 'K'
  hash ^= password.length;
  return (hash & 0xffff).toString(16).toUpperCase().padStart(4, "0");
}

/**
 * Adds <sheetProtection> to a worksheet XML so its (default-locked) cells can't
 * be edited once the file is opened. Placed after sheetData/sheetCalcPr per the
 * worksheet schema order. No-op if already protected.
 */
function protectSheetXml(xml: string, password?: string): string {
  if (/<sheetProtection\b/.test(xml)) return xml;
  const attrs = [
    password ? `password="${legacyPasswordHash(password)}"` : null,
    'sheet="1"',
    'objects="1"',
    'scenarios="1"',
  ]
    .filter(Boolean)
    .join(" ");
  const protection = `<sheetProtection ${attrs}/>`;
  // Schema order: sheetProtection follows sheetData (and sheetCalcPr, if present).
  return xml.replace(
    /(<\/sheetData>|<sheetData\s*\/>)((?:<sheetCalcPr[^>]*\/>)?)/,
    `$1$2${protection}`
  );
}

/** Sheet(s) locked on export, and the password Excel prompts for to unlock. */
export interface SheetProtection {
  sheets: string[];
  password?: string;
}

async function setFullCalcOnLoad(zip: JSZip): Promise<void> {
  const wbFile = zip.file("xl/workbook.xml");
  if (!wbFile) return;
  let xml = await wbFile.async("string");
  if (/<calcPr\b/.test(xml)) {
    xml = xml.replace(/<calcPr\b([^>]*?)(\/?)>/, (_m, attrs: string, selfClose: string) => {
      const cleaned = attrs.replace(/\s*fullCalcOnLoad="[^"]*"/, "");
      return `<calcPr${cleaned} fullCalcOnLoad="1"${selfClose}>`;
    });
  } else {
    // Schema order: calcPr follows definedNames when present, else sheets.
    const anchor = xml.includes("</definedNames>") ? "</definedNames>" : "</sheets>";
    xml = xml.replace(anchor, `${anchor}<calcPr fullCalcOnLoad="1"/>`);
  }
  zip.file("xl/workbook.xml", xml);
}

async function removeCalcChain(zip: JSZip): Promise<void> {
  if (!zip.file("xl/calcChain.xml")) return;
  zip.remove("xl/calcChain.xml");
  const contentTypes = zip.file("[Content_Types].xml");
  if (contentTypes) {
    const xml = await contentTypes.async("string");
    zip.file("[Content_Types].xml", xml.replace(/<Override\b[^>]*calcChain\.xml[^>]*\/>/g, ""));
  }
  const wbRels = zip.file("xl/_rels/workbook.xml.rels");
  if (wbRels) {
    const xml = await wbRels.async("string");
    zip.file("xl/_rels/workbook.xml.rels", xml.replace(/<Relationship\b[^>]*calcChain\.xml[^>]*\/>/g, ""));
  }
}

export interface CellRestyle {
  /** Worksheet name; null → the workbook's first sheet. */
  sheet: string | null;
  cell: string;
  /** Style index (the cell's `s` attribute) to apply; content untouched. */
  styleIndex: string;
}

/** Style index per column letter for one row (for copying a row's look). */
export async function readRowStyles(
  buf: ArrayBuffer,
  sheetName: string,
  row: number
): Promise<Map<string, string>> {
  const zip = await loadZipForRead(buf);
  const { pathByName } = await readSheetMapCached(zip);
  const path = resolveSheetPath(pathByName, sheetName);
  const out = new Map<string, string>();
  if (!path || !zip.file(path)) return out;
  const doc = new DOMParser().parseFromString(await zip.file(path)!.async("string"), "application/xml");
  const cellRe = new RegExp(`^([A-Z]+)${row}$`);
  for (const c of Array.from(doc.getElementsByTagName("c"))) {
    const m = c.getAttribute("r")?.match(cellRe);
    const s = c.getAttribute("s");
    if (m && s) out.set(m[1], s);
  }
  return out;
}

function restyleCellsXml(xml: string, items: Array<{ cell: string; styleIndex: string }>): string {
  for (const item of items) {
    const { row } = splitAddr(item.cell);
    const rowEl = matchElement(xml, "row", row);
    if (!rowEl) continue;
    const cellEl = matchElement(rowEl.text, "c", item.cell);
    if (!cellEl) continue;
    const openTag = cellEl.openTag;
    const newOpen = /\bs="[^"]*"/.test(openTag)
      ? openTag.replace(/\bs="[^"]*"/, `s="${item.styleIndex}"`)
      : openTag.replace(/\s*(\/?)>$/, ` s="${item.styleIndex}"$1>`);
    const newCell = newOpen + cellEl.text.slice(openTag.length);
    const newRow = spliceAt(rowEl.text, cellEl.start, cellEl.text.length, newCell);
    xml = spliceAt(xml, rowEl.start, rowEl.text.length, newRow);
  }
  return xml;
}

export interface SheetImage {
  data: ArrayBuffer;
  ext: "jpeg" | "png";
  /** Zero-based anchor cells; the image stretches from `from` to `to`. */
  fromCol: number;
  fromRow: number;
  toCol: number;
  toRow: number;
  /**
   * Absolute frame in EMU. Without an explicit <a:xfrm> extent Excel renders
   * the picture at its natural size instead of stretching to the anchor box.
   */
  emu?: { x: number; y: number; cx: number; cy: number };
  /**
   * Source-crop insets (1/1000 %), so Excel center-crops the picture to the
   * frame's aspect instead of distorting it to fill. Deterministic, canvas-free.
   */
  srcRect?: { l: number; t: number; r: number; b: number };
  name?: string;
}

const XDR_NS = "http://schemas.openxmlformats.org/drawingml/2006/spreadsheetDrawing";
const A_NS = "http://schemas.openxmlformats.org/drawingml/2006/main";
const R_NS = "http://schemas.openxmlformats.org/officeDocument/2006/relationships";

function anchorXml(img: SheetImage, relId: string, id: number): string {
  const name = escapeXml(img.name ?? `Survey Photo ${id}`);
  const xfrm = img.emu
    ? `<a:xfrm><a:off x="${img.emu.x}" y="${img.emu.y}"/><a:ext cx="${img.emu.cx}" cy="${img.emu.cy}"/></a:xfrm>`
    : "";
  const srcRect = img.srcRect
    ? `<a:srcRect l="${img.srcRect.l}" t="${img.srcRect.t}" r="${img.srcRect.r}" b="${img.srcRect.b}"/>`
    : "";
  return (
    `<xdr:twoCellAnchor editAs="oneCell">` +
    `<xdr:from><xdr:col>${img.fromCol}</xdr:col><xdr:colOff>19050</xdr:colOff><xdr:row>${img.fromRow}</xdr:row><xdr:rowOff>19050</xdr:rowOff></xdr:from>` +
    `<xdr:to><xdr:col>${img.toCol}</xdr:col><xdr:colOff>0</xdr:colOff><xdr:row>${img.toRow}</xdr:row><xdr:rowOff>0</xdr:rowOff></xdr:to>` +
    `<xdr:pic><xdr:nvPicPr><xdr:cNvPr id="${id}" name="${name}"/><xdr:cNvPicPr><a:picLocks noChangeAspect="1"/></xdr:cNvPicPr></xdr:nvPicPr>` +
    `<xdr:blipFill><a:blip xmlns:r="${R_NS}" r:embed="${relId}"/>${srcRect}<a:stretch><a:fillRect/></a:stretch></xdr:blipFill>` +
    `<xdr:spPr>${xfrm}<a:prstGeom prst="rect"><a:avLst/></a:prstGeom></xdr:spPr></xdr:pic>` +
    `<xdr:clientData/></xdr:twoCellAnchor>`
  );
}

/**
 * Inserts pictures anchored to cell ranges of one sheet, extending the
 * sheet's existing drawing part or creating one. Same zip-surgery philosophy
 * as the cell patcher: nothing outside the touched parts changes.
 */
export async function insertSheetImages(
  buf: ArrayBuffer,
  sheetName: string,
  images: SheetImage[]
): Promise<Blob> {
  const zip = await JSZip.loadAsync(buf);
  const { pathByName } = await readSheetMap(zip);
  const sheetPath = resolveSheetPath(pathByName, sheetName);
  if (!sheetPath || images.length === 0) {
    return zip.generateAsync({ type: "blob", compression: "DEFLATE" });
  }

  // Next free media index across the workbook.
  let mediaIdx = 1;
  zip.forEach((p) => {
    const m = p.match(/^xl\/media\/image(\d+)\./);
    if (m) mediaIdx = Math.max(mediaIdx, Number(m[1]) + 1);
  });

  const sheetBase = sheetPath.split("/").pop()!;
  const sheetRelsPath = `xl/worksheets/_rels/${sheetBase}.rels`;
  let sheetRels = zip.file(sheetRelsPath)
    ? await zip.file(sheetRelsPath)!.async("string")
    : `<?xml version="1.0" encoding="UTF-8" standalone="yes"?><Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships"></Relationships>`;

  // Locate (or create) the sheet's drawing part.
  const drawingRel = sheetRels.match(
    /<Relationship\b[^>]*Type="[^"]*\/drawing"[^>]*Target="([^"]+)"[^>]*>/
  );
  let drawingPath: string;
  if (drawingRel) {
    const target = drawingRel[1];
    drawingPath = target.startsWith("/")
      ? target.slice(1)
      : `xl/${target.replace(/^(\.\.\/)+/, "")}`;
  } else {
    let drawingIdx = 1;
    zip.forEach((p) => {
      const m = p.match(/^xl\/drawings\/drawing(\d+)\.xml$/);
      if (m) drawingIdx = Math.max(drawingIdx, Number(m[1]) + 1);
    });
    drawingPath = `xl/drawings/drawing${drawingIdx}.xml`;

    // New sheet relationship + <drawing> element in the worksheet.
    const relIds = Array.from(sheetRels.matchAll(/\bId="rId(\d+)"/g)).map((m) => Number(m[1]));
    const newRelId = `rId${Math.max(0, ...relIds) + 1}`;
    sheetRels = sheetRels.replace(
      "</Relationships>",
      `<Relationship Id="${newRelId}" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/drawing" Target="../drawings/drawing${drawingIdx}.xml"/></Relationships>`
    );
    let sheetXml = await zip.file(sheetPath)!.async("string");
    const drawingEl = `<drawing r:id="${newRelId}"/>`;
    sheetXml = sheetXml.includes("<extLst")
      ? sheetXml.replace("<extLst", `${drawingEl}<extLst`)
      : sheetXml.replace("</worksheet>", `${drawingEl}</worksheet>`);
    zip.file(sheetPath, sheetXml);

    zip.file(
      drawingPath,
      `<?xml version="1.0" encoding="UTF-8" standalone="yes"?><xdr:wsDr xmlns:xdr="${XDR_NS}" xmlns:a="${A_NS}"></xdr:wsDr>`
    );
    const contentTypes = await zip.file("[Content_Types].xml")!.async("string");
    if (!contentTypes.includes(`/${drawingPath}`)) {
      zip.file(
        "[Content_Types].xml",
        contentTypes.replace(
          "</Types>",
          `<Override PartName="/${drawingPath}" ContentType="application/vnd.openxmlformats-officedocument.drawing+xml"/></Types>`
        )
      );
    }
  }
  zip.file(sheetRelsPath, sheetRels);

  // Drawing rels: append one image relationship per picture.
  const drawingRelsPath = `xl/drawings/_rels/${drawingPath.split("/").pop()}.rels`;
  let drawingRels = zip.file(drawingRelsPath)
    ? await zip.file(drawingRelsPath)!.async("string")
    : `<?xml version="1.0" encoding="UTF-8" standalone="yes"?><Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships"></Relationships>`;
  const usedRelIds = Array.from(drawingRels.matchAll(/\bId="rId(\d+)"/g)).map((m) => Number(m[1]));
  let nextRelNum = Math.max(0, ...usedRelIds) + 1;

  let drawingXml = await zip.file(drawingPath)!.async("string");
  const usedPicIds = Array.from(drawingXml.matchAll(/<xdr:cNvPr\b[^>]*\bid="(\d+)"/g)).map((m) =>
    Number(m[1])
  );
  let nextPicId = Math.max(100, ...usedPicIds) + 1;

  let anchors = "";
  const extensions = new Set<string>();
  for (const img of images) {
    const mediaName = `image${mediaIdx++}.${img.ext}`;
    // STORE, not DEFLATE: JPEG/PNG bytes don't compress, and deflating tens of
    // MB of photos is pure CPU — enough to trip the edge worker's budget.
    zip.file(`xl/media/${mediaName}`, img.data, { compression: "STORE" });
    extensions.add(img.ext);
    const relId = `rId${nextRelNum++}`;
    drawingRels = drawingRels.replace(
      "</Relationships>",
      `<Relationship Id="${relId}" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/image" Target="../media/${mediaName}"/></Relationships>`
    );
    anchors += anchorXml(img, relId, nextPicId++);
  }
  drawingXml = drawingXml.replace(/<\/xdr:wsDr>\s*$/, `${anchors}</xdr:wsDr>`);
  zip.file(drawingPath, drawingXml);
  zip.file(drawingRelsPath, drawingRels);

  // Content types: media extensions must have a Default entry.
  let contentTypes = await zip.file("[Content_Types].xml")!.async("string");
  for (const ext of extensions) {
    if (!new RegExp(`Extension="${ext}"`).test(contentTypes)) {
      contentTypes = contentTypes.replace(
        "</Types>",
        `<Default Extension="${ext}" ContentType="image/${ext}"/></Types>`
      );
    }
  }
  zip.file("[Content_Types].xml", contentTypes);

  return zip.generateAsync({
    type: "blob",
    compression: "DEFLATE",
    mimeType: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
}

/**
 * Stamps the writes into a copy of the template, touching nothing else.
 * Writes without a sheet of their own go to `defaultSheet` (the workbook's
 * first sheet when not given); writes naming a missing sheet are skipped.
 */
export async function stampXlsxTemplate(
  templateBuf: ArrayBuffer,
  writes: CellWrite[],
  defaultSheet?: string | null,
  restyles?: CellRestyle[],
  protect?: SheetProtection
): Promise<{ data: Blob; written: number }> {
  const zip = await JSZip.loadAsync(templateBuf);
  const { names, pathByName } = await readSheetMap(zip);
  const fallback = defaultSheet ?? names[0];

  const patchesBySheet = new Map<
    string,
    Array<{ cell: string; value: string | number | null; force?: boolean }>
  >();
  let written = 0;
  for (const write of writes) {
    const sheetName = write.sheet ?? fallback;
    // Key by resolved zip path, not the requested name — a sheet referenced as
    // both "…- Summary" and "…— Summary" must collect into one patch batch.
    const path = resolveSheetPath(pathByName, sheetName);
    if (!path) {
      console.warn(`[excel export] Worksheet "${sheetName}" not found in template; skipping ${write.cell}.`);
      continue;
    }
    let patches = patchesBySheet.get(path);
    if (!patches) patchesBySheet.set(path, (patches = []));
    patches.push({ cell: write.cell, value: write.value, force: write.force });
    if (write.value !== null) written++;
  }

  let formulaTouched = false;
  for (const [path, patches] of patchesBySheet) {
    const sheetFile = zip.file(path);
    if (!sheetFile) continue;
    const result = patchSheetXml(await sheetFile.async("string"), patches);
    zip.file(path, result.xml);
    formulaTouched ||= result.formulaTouched;
  }
  if (formulaTouched) await removeCalcChain(zip);

  // Style-only pass (after value patches, which preserve each cell's style).
  if (restyles?.length) {
    const bySheet = new Map<string, Array<{ cell: string; styleIndex: string }>>();
    for (const r of restyles) {
      const sheetName = r.sheet ?? fallback;
      const path = resolveSheetPath(pathByName, sheetName);
      if (!path) continue;
      let items = bySheet.get(path);
      if (!items) bySheet.set(path, (items = []));
      items.push({ cell: r.cell, styleIndex: r.styleIndex });
    }
    for (const [path, items] of bySheet) {
      const sheetFile = zip.file(path);
      if (!sheetFile) continue;
      zip.file(path, restyleCellsXml(await sheetFile.async("string"), items));
    }
  }

  // Lock requested sheets so their (default-locked) cells become read-only
  // once the recipient opens the file — the survey responses can't be edited.
  if (protect?.sheets.length) {
    for (const sheetName of protect.sheets) {
      const path = resolveSheetPath(pathByName, sheetName);
      const sheetFile = path ? zip.file(path) : null;
      if (!path || !sheetFile) {
        console.warn(`[excel export] Cannot lock "${sheetName}" — not found in template.`);
        continue;
      }
      zip.file(path, protectSheetXml(await sheetFile.async("string"), protect.password));
    }
  }

  // Template formulas reference the cells we just stamped; without a full
  // recalc on open, Excel shows their stale cached values (all the pre-export
  // zeros) instead of recomputing.
  if (written > 0) await setFullCalcOnLoad(zip);

  const data = await zip.generateAsync({
    type: "blob",
    compression: "DEFLATE",
    mimeType: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  return { data, written };
}
